<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import AccordionTrigger from '@/components/accordion/AccordionTrigger.vue'
import AccordionBody from '@/components/accordion/AccordionBody.vue'

export default defineComponent({
  name: 'FollowGuide',

  components: {
    AccordionTrigger,
    AccordionBody,
  },

  props: {
    /**
     * アコーディオン機能を有効にするかどうかを表すフラグ
     * @description true: 有効にする, false: 有効にしない
     */
    hasAccordion: {
      type: Boolean,
      default: true,
    },

    /**
     * 初期の開閉状態を表すフラグ
     * @description true: 開いている, false: 閉じている
     */
    initOpen: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // constants
    const TOGGLE_STATE_CLASS_NAME = {
      OPEN: 'is-open',
      CLOSE: 'is-close',
    } as const

    /**
     * 開閉状態を表すフラグ
     * 開閉が許可されていない、または初期状態で開いているか場合は true
     */
    const isOpen = ref(!props.hasAccordion || props.initOpen)

    /**
     * 開閉に応じて変化するラベル
     */
    const toggleLabel = computed<string>(() => {
      const LABEL_PREFIX = '「フォロー機能とは」を'
      return isOpen.value ? `${LABEL_PREFIX}閉じる` : `${LABEL_PREFIX}見る`
    })

    return {
      // constants
      TOGGLE_STATE_CLASS_NAME,

      // date
      isOpen,

      // computed
      toggleLabel,
    }
  },
})
</script>

<template>
  <div
    :class="[
      'l-unit__followGuide',
      { 'is-open': isOpen },
    ]"
    :data-accordion="hasAccordion">
    <AccordionBody
      :open-state="isOpen"
      class="l-unit__followGuide--description">
      <section
        :class="[
          'f-section__followGuide',
          { 'is-open': isOpen },
        ]">
        <div class="f-section__followGuide--body">
          <div class="o-heading__followGuide">
            <h2 class="o-heading__followGuide--item">
              フォロー機能の使い方
            </h2>
          <!-- /.o-heading__followGuide --></div>

          <div
            class="l-cols is-col2"
            data-breakpoint="sp">
            <div class="l-cols--col">
              <div class="l-unit__followStep">
                <div class="l-unit__followStep--header">
                  <div class="o-heading__followStep">
                    <h3 class="o-heading__followStep--item">
                      <span class="o-heading__followStep--label is-primary">
                        STEP 1
                      </span>
                      <span class="o-heading__followStep--label is-secondary">
                        <span class="span u-text__block">
                          関心のある特集または
                        </span>
                        <span class="span u-text__block">
                          用語をフォローする
                        </span>
                      </span>
                    </h3>
                  <!-- /.o-heading__followStep --></div>
                <!-- /.l-unit__followStep header --></div>

                <div class="l-unit__followStep--image">
                  <img
                    class="img"
                    src="@/images/mypage/bg-follow-step1_01.svg"
                    loading="lazy"
                    alt="「フォローする」ボタンを押してフォローをすることを説明する画像">
                <!-- /.l-unit__followStep image --></div>

                <div class="l-unit__followStep--description">
                  <p class="p u-text__small">
                    各特集/用語の詳細ページからフォローができます。
                  </p>
                <!-- /.l-unit__followStep description --></div>
              <!-- /.l-unit__followStep --></div>
            <!-- /.l-cols col --></div>

            <div class="l-cols--col">
              <div class="l-unit__followStep">
                <div class="l-unit__followStep--header">
                  <div class="o-heading__followStep">
                    <h3 class="o-heading__followStep--item">
                      <span class="o-heading__followStep--label is-primary">
                        STEP 2
                      </span>
                      <span class="o-heading__followStep--label is-secondary">
                        記事をまとめて読む
                      </span>
                    </h3>
                  <!-- /.o-heading__followStep --></div>
                <!-- /.l-unit__followStep header --></div>

                <div class="l-unit__followStep--image">
                  <img
                    class="img"
                    src="@/images/mypage/bg-follow-step2_01.svg"
                    loading="lazy"
                    alt="記事を様々なデバイスでまとめ読みできることを説明する画像">
                <!-- /.l-unit__followStep image --></div>

                <div class="l-unit__followStep--description">
                  <p class="p u-text__small">
                    フォローした特集/用語の関連情報をマイページからまとめ読みできます。
                  </p>
                <!-- /.l-unit__followStep description --></div>
              <!-- /.l-unit__followStep --></div>
            <!-- /.l-cols col --></div>
          <!-- /.l-cols --></div>
        <!-- /.f-section__followGuide body --></div>
      <!-- /.f-section__followGuide --></section>
    </AccordionBody>

    <div
      v-if="hasAccordion"
      class="l-unit__followGuide--action">
      <div
        :class="[
          'o-action__toggle',
          isOpen ? TOGGLE_STATE_CLASS_NAME.OPEN : TOGGLE_STATE_CLASS_NAME.CLOSE
        ]">
        <AccordionTrigger
          v-model:open-state="isOpen"
          tag-name="button"
          class="o-action__toggle--item">
            {{ toggleLabel }}
        </AccordionTrigger>
      <!-- /.o-action__toggle --></div>
    <!-- /.l-unit__followGuide action --></div>
  <!-- /.l-unit__followGuide --></div>
</template>

<style lang="sass" scoped>
.f-section__followGuide
  padding: 2em 1em
  background-color: $bg-white
  opacity: 0
  +transition($duration: $transition-long)

  &.is-open
    opacity: 1

.l-unit__followGuide
  $root: &
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--action
    +transition

  &.is-open
    #{$root}--action
      margin-top: 1em

.l-unit__followStep
  & &--header
    margin-bottom: changeSizePx2Em(12)

  & &--description
    display: flex
    flex-direction: column
    align-items: center
    margin-top: changeSizePx2Em(8)

.o-heading__followGuide
  display: flex
  flex-direction: column
  align-items: center
  margin-bottom: 2em

  &:last-child
    margin-bottom: 0

  & &--item
    font:
      size: $font-large
      weight: bold

    $contents-width-px: 180
    $border-width-px: 40
    &::after
      content: ""
      width: getRelativeValue($border-width-px, $contents-width-px)
      height: 2px
      display: block
      margin: changeSizePx2Em(20) auto 0
      background-color: $bg-dark-orange

.o-heading__followStep
  & &--item
    font-weight: bold

  & &--label
    display: block
    text-align: center

    &.is-primary
      line-height: 1
      color: $font-dark-orange
      font:
        family: 'Montserrat', sans-serif
        size: $font-small
      margin-bottom: 0.5em

    $min-line: 2
    $line-height-px: 24
    $line-height: math.div($line-height-px, $font-normal-px)
    $min-height-px: $font-normal-px * $line-height * $min-line
    &.is-secondary
      min-height: changeSizePx2Em($min-height-px)
      line-height: $line-height

.o-action__toggle
  $root: &

  & &--item
    display: flex
    align-items: center
    line-height: 1
    color: $font-dark-blue
    font-size: $font-small

    &::before
      margin:
        right: 0.5em
        left: 0.2em // adjust the excess on the left side using CSS rotate.

    +mouseover
      text-decoration: underline

  &.is-open
    #{$root}--item
      &::before
        +iconArrow(up, $font-dark-blue)

  &.is-close
    #{$root}--item
      &::before
        +iconArrow(down, $font-dark-blue)
</style>
